/**
 * Golf Outing — core scoring logic
 * 3-day weekend format:
 *   Friday AM:   stroke play (individual, seeding round 1)
 *   Friday PM:   4-man scramble (teams from Friday AM order)
 *   Saturday AM: stroke play (individual, seeding round 2)
 *   Saturday PM: 4-man scramble (teams from Saturday AM order)
 *   Sunday AM:   4-man scramble (teams seeded by Fri AM + Sat AM combined)
 */

/**
 * Effective par for a hole, gendered. Female players read women's par when
 * populated; everyone else (male, nonbinary, prefer-not-to-say, unknown)
 * reads men's. Falls back to men's when women's value is NULL — courses
 * that haven't had women's data populated yet still produce a usable
 * number rather than NaN.
 *
 * Pass the full hole record (`course_holes` row shape) — both par_women
 * and par need to be readable.
 */
export function effectivePar(hole, gender) {
  if (!hole) return 4
  if (gender === 'female' && hole.par_women != null) return hole.par_women
  return hole.par ?? 4
}

/**
 * Same fallback semantics for the stroke index — used by handicap-stroke
 * allocation + tiebreakers.
 */
export function effectiveHandicapRank(hole, gender) {
  if (!hole) return null
  if (gender === 'female' && hole.handicap_rank_women != null) return hole.handicap_rank_women
  return hole.handicap_rank
}

/**
 * Total par across an entire layout, gendered. Sum of effectivePar over the
 * passed holes. Used by display headers (e.g. "Heather Ridge · Par 36" for
 * a female playing women's tees vs Par 35 for men's).
 */
export function totalParForGender(holes, gender) {
  if (!Array.isArray(holes) || holes.length === 0) return null
  return holes.reduce((sum, h) => sum + effectivePar(h, gender), 0)
}

/**
 * Sort players by gross score with handicap-hole tiebreaker.
 * Used for per-round leaderboards AND combined totals for Sunday seeding.
 *
 * @param {Array} scorecards  - [{ player_id, player_name, total_score, hole_scores }]
 * @param {Array} courseHoles - [{ hole_number, handicap_rank }]
 * @returns {Array} sorted with finishing_position added
 */
export function sortPlayersByScore(scorecards, courseHoles = []) {
  const holesByHandicap = [...courseHoles]
    .sort((a, b) => a.handicap_rank - b.handicap_rank)
    .map(h => h.hole_number)

  const sorted = [...scorecards].sort((a, b) => {
    // Not started always goes to the bottom
    if (a.not_started && !b.not_started) return 1
    if (b.not_started && !a.not_started) return -1
    if (a.not_started && b.not_started) return (a.player_name || '').localeCompare(b.player_name || '')
    // Primary: total score (lower is better)
    if (a.total_score !== b.total_score) return a.total_score - b.total_score

    // Tiebreaker 1: finished rounds rank above in-progress
    const aFin = a.is_complete || a.holes_completed >= 18
    const bFin = b.is_complete || b.holes_completed >= 18
    if (aFin && !bFin) return -1
    if (bFin && !aFin) return 1

    // Tiebreaker 2: among in-progress, sort by holes played
    // If score is over par (positive): more holes played = ranked higher (closer to finishing)
    // If score is even or under par (0 or negative): fewer holes played = ranked higher
    //   (they're leading without having used all their holes yet)
    const aHoles = a.holes_completed || Object.keys(a.hole_scores || {}).length
    const bHoles = b.holes_completed || Object.keys(b.hole_scores || {}).length
    if (!aFin && !bFin && aHoles !== bHoles) {
      const score = a.total_score // same for both since we passed total_score check
      if (score > 0) return bHoles - aHoles  // over par: more holes played ranks higher
      else return aHoles - bHoles             // even/under: fewer holes played ranks higher
    }

    // Tiebreaker 3: handicap hole order (for final locked standings)
    for (const holeNum of holesByHandicap) {
      const scoreA = a.hole_scores?.[String(holeNum)] ?? 99
      const scoreB = b.hole_scores?.[String(holeNum)] ?? 99
      if (scoreA !== scoreB) return scoreA - scoreB
    }

    // Last resort: alphabetical
    return (a.player_name || '').localeCompare(b.player_name || '')
  })

  // Assign positions — tied players share the first position number in their group
  // Next non-tied player skips positions equal to tie group size
  // e.g. 2-way tie at #4 → both show 4 (or blank), next player is #6
  return sorted.map((sc, idx) => {
    // Find start of this player's tie group (first player with same score)
    let groupStart = idx
    while (groupStart > 0 && sorted[groupStart - 1].total_score === sc.total_score &&
           !sorted[groupStart - 1].not_started && !sc.not_started) {
      groupStart--
    }
    const position = groupStart + 1  // 1-based position of the tie group leader
    const isTied = groupStart < idx  // not the first in the group
    return { ...sc, finishing_position: position, is_tied: isTied }
  })
}

/**
 * Sort players by COMBINED Fri AM + Sat AM score for Sunday seeding.
 *
 * Tiebreaker: for each handicap rank 1–18, add the player's score on
 * Friday's #N handicap hole + Saturday's #N handicap hole. Lower sum wins.
 * Example: TB#1 = Hemlock hole 7 score + Manistee hole 5 score (both hdcp #1)
 *
 * @param {Array} combined      - [{ player_id, player_name, combined_score, friday_holes, saturday_holes }]
 * @param {Array} fridayHoles   - [{ hole_number, handicap_rank }]
 * @param {Array} saturdayHoles - [{ hole_number, handicap_rank }]
 */
export function sortCombinedForSunday(combined, fridayHoles = [], saturdayHoles = []) {
  // Build map: handicap_rank -> hole_number for each course
  const friHoleByRank = {}
  fridayHoles.forEach(h => { friHoleByRank[h.handicap_rank] = h.hole_number })

  const satHoleByRank = {}
  saturdayHoles.forEach(h => { satHoleByRank[h.handicap_rank] = h.hole_number })

  const maxRank = Math.max(
    ...Object.keys(friHoleByRank).map(Number),
    ...Object.keys(satHoleByRank).map(Number),
    18
  )

  const sorted = [...combined].sort((a, b) => {
    if (a.combined_score !== b.combined_score) return a.combined_score - b.combined_score

    const friA = parseHoles(a.friday_holes)
    const friB = parseHoles(b.friday_holes)
    const satA = parseHoles(a.saturday_holes)
    const satB = parseHoles(b.saturday_holes)

    // Walk handicap ranks 1..18
    // TB for rank N = (Fri hdcp-N hole score) + (Sat hdcp-N hole score)
    for (let rank = 1; rank <= maxRank; rank++) {
      const friHole = friHoleByRank[rank]
      const satHole = satHoleByRank[rank]

      const friScoreA = friHole ? (friA[String(friHole)] ?? 99) : 0
      const friScoreB = friHole ? (friB[String(friHole)] ?? 99) : 0
      const satScoreA = satHole ? (satA[String(satHole)] ?? 99) : 0
      const satScoreB = satHole ? (satB[String(satHole)] ?? 99) : 0

      const sumA = friScoreA + satScoreA
      const sumB = friScoreB + satScoreB

      if (sumA !== sumB) return sumA - sumB
    }

    return (a.player_name || '').localeCompare(b.player_name || '')
  })

  return sorted.map((p, idx) => ({ ...p, seed_position: idx + 1 }))
}

function parseHoles(h) {
  if (!h) return {}
  if (typeof h === 'string') { try { return JSON.parse(h) } catch { return {} } }
  return h
}

/**
 * Generate scramble teams via snake draft.
 * Works for 16 (4 teams), 20 (5 teams), 24 (6 teams).
 * Team 1: picks 1, 2N, 2N+1, 4N  (e.g. 20 players: 1,10,11,20)
 */
export function generateScrambleTeams(sortedPlayers, { forceTeamCount = null } = {}) {
  const n = sortedPlayers.length
  if (n < 4) throw new Error(`Need at least 4 players to form a team.`)

  // Determine team count: prefer even teams of 4, allow override for unusual counts
  let teamCount
  if (forceTeamCount) {
    teamCount = forceTeamCount
  } else if (n % 4 === 0) {
    teamCount = n / 4
  } else {
    // Round up — some teams will have 3 players
    teamCount = Math.ceil(n / 4)
  }

  const teams = Array.from({ length: teamCount }, (_, i) => ({
    team_number: i + 1,
    players: [],
    finishing_positions: [],
  }))

  // Snake draft: rounds alternate direction
  const rounds = [
    [...Array(teamCount).keys()],
    [...Array(teamCount).keys()].reverse(),
    [...Array(teamCount).keys()],
    [...Array(teamCount).keys()].reverse(),
  ]

  let idx = 0
  for (const round of rounds) {
    for (const teamIdx of round) {
      if (idx < sortedPlayers.length) {
        teams[teamIdx].players.push(sortedPlayers[idx])
        teams[teamIdx].finishing_positions.push(idx + 1)
        idx++
      }
    }
  }
  return teams
}

export function calculateTotal(holeScores) {
  if (!holeScores) return 0
  const scores = typeof holeScores === 'string' ? JSON.parse(holeScores) : holeScores
  return Object.values(scores).reduce((sum, s) => sum + (parseInt(s) || 0), 0)
}

export function scoreVsPar(total, par) {
  if (!total || !par) return null
  const diff = total - par
  return diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`
}

export function holeScoreClass(score, par) {
  if (!score || !par) return ''
  const diff = score - par
  if (diff <= -2) return 'eagle'
  if (diff === -1) return 'birdie'
  if (diff === 0) return 'par'
  if (diff === 1) return 'bogey'
  if (diff === 2) return 'double'
  return 'triple'
}

export function formatTime(t) {
  if (!t) return ''
  const [h, m] = t.split(':')
  const hour = parseInt(h)
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`
}

export function formatDate(d) {
  if (!d) return ''
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  })
}

// User-friendly labels for the derived lifecycle status (deriveEventStatus).
// Used for display badges across EventsPage / HomePage / LeaderboardPage.
export const EVENT_STATUS_LABELS = {
  draft: 'Draft',
  registration: 'Registration',
  upcoming: 'Upcoming',
  in_progress: 'In Progress',
  complete: 'Complete',
}

// Derive an event's DISPLAY lifecycle status from its stored markers plus the
// (optional) loaded rounds/scores — so a past outing reads 'Complete' and a
// new one progresses registration -> upcoming -> in_progress -> complete
// without a hand-set status column (D-341).
//
//   event  — the events row (reads completed_at, published_at, event_date)
//   rounds — event_rounds for this event ([] when a caller hasn't loaded them);
//            each has play_date. Falls back to event.event_date when empty.
//   scores — this event's round_scores ([] when unloaded); each needs is_complete.
//
// completed_at is checked FIRST so historical completes resolve correctly
// whether or not rounds/scores are loaded. The legacy `status` column is
// intentionally NOT read (Phase-3 cutover, D-341) — Phase-2 backfilled
// completed_at for every historical outing, so it is the sole 'done' marker.
export function deriveEventStatus(event, rounds = [], scores = []) {
  if (!event) return null
  if (event.completed_at != null) return 'complete'
  if (event.published_at == null) return 'draft'

  const playDates = (Array.isArray(rounds) ? rounds : [])
    .map(r => r && r.play_date)
    .filter(Boolean)
  const firstDate = playDates.length > 0
    ? playDates.reduce((min, d) => (d < min ? d : min))
    : (event.event_date || null)

  const startMs = firstDate != null
    ? Date.parse(/T/.test(firstDate) ? firstDate : `${firstDate}T00:00:00`)
    : NaN
  const beforeStart = firstDate != null && !Number.isNaN(startMs) && Date.now() < startMs

  const scoreList = Array.isArray(scores) ? scores : []
  const hasScores = scoreList.length > 0
  const allComplete = hasScores && scoreList.every(s => s && s.is_complete)

  if (!hasScores && (firstDate == null || beforeStart)) return 'registration'
  if (beforeStart) return 'upcoming'
  if (hasScores && !allComplete) return 'in_progress'
  return 'upcoming'
}

// Convert a TournyFlex Index (a WHS-style Handicap Index) to a Course
// Handicap for a specific tee. WHS formula:
//   Course Handicap = Index × (Slope / 113) + (Rating − Par)
// Capped at 54 (WHS ceiling since 2020). When slope/rating/par snapshots
// are missing (pre-2026 rounds, courses without WHS data), we fall back
// to the raw index — same behaviour as before this helper existed, so
// legacy rounds don't silently shift.
//
// This is only step 2 of 3 in adjusted-score math:
//   1. Round → Index differential (uses slope/113) — done at save time
//   2. Index → Course Handicap (uses slope/113 + rating−par) — THIS FN
//   3. Course Handicap → per-hole strokes — strokesOnHole below
export function resolveCourseHandicap(index, slope, rating, par) {
  if (index == null) return null
  const idx = Number(index)
  if (!Number.isFinite(idx)) return null
  const s = Number(slope), r = Number(rating), pa = Number(par)
  const haveAllRatings = [s, r, pa].every(Number.isFinite) && s > 0
  const ch = haveAllRatings ? idx * (s / 113) + (r - pa) : idx
  return Math.min(54, Math.max(0, Math.round(ch)))
}

// Per-hole handicap-stroke allocation. Standard USGA rules:
//   * Player receives 1 stroke per hole with handicap_rank <= (courseHcp % 18)
//   * Plus one additional stroke per hole for every full cycle of the hole
//     count (i.e. course hcp of 20 on 18 holes → 1 stroke on every hole
//     PLUS a second on holes ranked 1 and 2).
// holeCount is passed explicitly so 9-hole rounds distribute over ranks 1..9.
//
// 9-hole convention: the stored handicap_before / starter_handicap_index is
// an 18-hole figure. For a 9-hole round the player only gets HALF that
// allocation, so we halve the raw input before distributing.
export function strokesOnHole(rawCourseHcp, handicapRank, holeCount = 18) {
  if (!rawCourseHcp || rawCourseHcp <= 0 || !handicapRank) return 0
  const courseHcp = holeCount === 9 ? Math.round(rawCourseHcp / 2) : rawCourseHcp
  const cycles = Math.floor(courseHcp / holeCount)
  const remainder = courseHcp % holeCount
  return cycles + (handicapRank <= remainder ? 1 : 0)
}

// Bucket the "how much handicap does this player get" question. Round entries
// carry an explicit handicap override (entered on PlayPage); otherwise fall
// back to whatever the player row has stashed. Returns a rounded integer or
// null if we truly have nothing to work with.
export function resolvePlayerHandicap(playerEntry, linkedProfile) {
  const raw = (playerEntry && playerEntry.handicap != null)
    ? playerEntry.handicap
    : linkedProfile?.tf_index_attested ?? linkedProfile?.tf_index_overall ?? linkedProfile?.starter_handicap_index
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return Math.round(n)
}
